import React, { useState, useEffect } from "react";
import {
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Grid,
    Typography,
    Stepper,
    Step,
    StepLabel,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Checkbox,
    FormControlLabel,
    LinearProgress,
    Box,
    Chip,
    CircularProgress,
    InputAdornment,
    OutlinedInput
} from "@material-ui/core";
import { Alert } from "@material-ui/lab";
import SearchIcon from "@material-ui/icons/Search";
import Clear from "@material-ui/icons/Clear";
import { makeStyles } from "@material-ui/core/styles";
import EnhancedSelect from "../../../components/EnhancedSelect";
import EnhancedTextField from "../../../components/EnhancedTextField";
import EnhancedAutocomplete from "../../../components/EnhancedAutocomplete";
import { useGlobalStyles } from "../../../utils/styles";
import { updateProduct, getProduct, createAndUploadProductBackup, getAllTags, getAllCategories, getAttributes as utilGetAttributes } from "../../../utils/utils";
import Auth from "../../../../auth";
import clsx from "clsx";

const BULK_UPDATE_EXCLUDED_ATTRIBUTES = ["category_id", "approved_by", "approval_status", "approval_date", 
    "delisted_by", "delisted_date", "sync_status", "listed_by", "listed_date", "category", "tags"];

const useStyles = makeStyles(theme => ({
    dialog: {
        "& .MuiDialog-paper": {
            maxWidth: "90vw",
            width: "900px",
            height: "80vh"
        },
        "& .MuiDialogContent-root": {
            display: "flex",
            flexDirection: "column"
        }
    },
    stepContent: {
        padding: "20px 0",
        minHeight: "400px",
        display: "flex",
        flexDirection: "column",
        height: "100%",
        overflow: "hidden"
    },
    attributeCard: {
        padding: "16px",
        margin: "8px 0",
        border: "1px solid #e0e0e0",
        borderRadius: "8px"
    },
    operatorColumn: {
        minWidth: "200px"
    },
    valueColumn: {
        minWidth: "300px"
    },
    progressContainer: {
        padding: "20px",
        textAlign: "center",
        display: "flex",
        flexDirection: "column",
        height: "100%",
        overflow: "hidden"
    },
    progressItem: {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        margin: "8px 0"
    },
    diffTable: {
        marginTop: "16px",
        "& .MuiTableCell-root": {
            fontSize: "0.875rem"
        }
    },
    diffOldValue: {
        color: "#d32f2f",
        textDecoration: "line-through",
        display: "block"
    },
    diffNewValue: {
        color: "#388e3c",
        display: "block",
        fontWeight: 500
    },
    skuCell: {
        fontWeight: 500,
        minWidth: "120px"
    },
    previewNote: {
        marginTop: "8px",
        fontStyle: "italic",
        color: "#666"
    },
    searchBar: {
        marginBottom: "16px",
        "& .MuiOutlinedInput-root": {
            backgroundColor: "#f5f5f5"
        }
    }
}));

const getOperatorsByType = (attributeType) => {
    switch (attributeType) {
        case "list":
            return [
                { label: "Replace with", value: "replace" },
                { label: "Add to existing", value: "add" },
                { label: "Clear field", value: "clear" }
            ];
        case "text":
            return [
                { label: "Replace with", value: "replace" },
                { label: "Add to existing", value: "add" },
                { label: "Clear field", value: "clear" }
            ];
        case "number":
            return [
                { label: "Replace with", value: "replace" },
                { label: "Increase by #", value: "increase_by_number" },
                { label: "Decrease by #", value: "decrease_by_number" },
                { label: "Increase by %", value: "increase_by_percent" },
                { label: "Decrease by %", value: "decrease_by_percent" },
                { label: "Clear field", value: "clear" }
            ];
        case "boolean":
        case "yesno":
            return [
                { label: "Replace with", value: "replace" },
                { label: "Set to Yes", value: "set_yes" },
                { label: "Set to No", value: "set_no" }
            ];
        case "image":
            return [
                { label: "Replace with", value: "replace" },
                { label: "Clear field", value: "clear" }
            ];
        case "video":
            return [
                { label: "Replace with URL", value: "replace" },
                { label: "Clear field", value: "clear" }
            ];
        case "document":
            return [
                { label: "Replace with URL", value: "replace" },
                { label: "Clear field", value: "clear" }
            ];
        case "tags":
            return [
                { label: "Replace with", value: "replace" },
                { label: "Add to existing", value: "add" },
                { label: "Remove specific tags", value: "remove" },
                { label: "Clear all tags", value: "clear" }
            ];
        case "category":
            return [
                { label: "Replace with", value: "replace" },
                { label: "Clear category", value: "clear" }
            ];
        default:
            return [
                { label: "Replace with", value: "replace" },
                { label: "Clear field", value: "clear" }
            ];
    }
};

const applyOperation = (currentValue, operator, newValue, attributeType) => {
    switch (operator) {
        case "clear":
            return null;
        case "set_yes":
            return true;
        case "set_no":
            return false;
        case "replace":
            return newValue;
        case "add":
            if (attributeType === "list" || attributeType === "tags") {
                const current = Array.isArray(currentValue) ? currentValue : [];
                const toAdd = Array.isArray(newValue) ? newValue : [newValue];
                return Array.from(new Set([...current, ...toAdd]));
            } else if (attributeType === "text") {
                return (currentValue || "") + (newValue || "");
            }
            return newValue;
        case "remove":
            if (attributeType === "tags") {
                const current = Array.isArray(currentValue) ? currentValue : [];
                const toRemove = Array.isArray(newValue) ? newValue : [newValue];
                return current.filter(tag => !toRemove.includes(tag));
            }
            return currentValue;
        case "increase_by_number":
            return (parseFloat(currentValue) || 0) + (parseFloat(newValue) || 0);
        case "decrease_by_number":
            return (parseFloat(currentValue) || 0) - (parseFloat(newValue) || 0);
        case "increase_by_percent":
            return (parseFloat(currentValue) || 0) * (1 + (parseFloat(newValue) || 0) / 100);
        case "decrease_by_percent":
            return (parseFloat(currentValue) || 0) * (1 - (parseFloat(newValue) || 0) / 100);
        default:
            return newValue;
    }
};

function BulkEditActions({ 
    open, 
    setOpen, 
    selectedProducts, 
    isRetailer, 
    currentCatalog, 
    setUpdating, 
    setError,
    onComplete 
}) {
    const classes = useStyles();
    const globalClasses = useGlobalStyles();
    
    const [activeStep, setActiveStep] = useState(0);
    const [selectedAttributes, setSelectedAttributes] = useState([]);
    const [attributeOperations, setAttributeOperations] = useState({});
    const [processing, setProcessing] = useState(false);
    const [progress, setProgress] = useState({});
    const [completed, setCompleted] = useState(false);
    const [productPreviews, setProductPreviews] = useState([]);
    const [loadingPreviews, setLoadingPreviews] = useState(false);
    const [transitioning, setTransitioning] = useState(false);
    const [availableTags, setAvailableTags] = useState([]);
    const [availableCategories, setAvailableCategories] = useState([]);
    const [availableAttributes, setAvailableAttributes] = useState([]);
    const [loadingMetadata, setLoadingMetadata] = useState(false);
    const [attributeSearchTerm, setAttributeSearchTerm] = useState("");
    const [backupProgress, setBackupProgress] = useState(0);
    const [backupTimestamp, setBackupTimestamp] = useState(null);

    const steps = ["Select Attributes", "Configure Operations", "Confirmation", "Processing"];

    useEffect(() => {
        if (open && (availableTags.length === 0 || availableCategories.length === 0 || availableAttributes.length === 0)) {
            loadMetadata();
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    // Animate backup progress to 25% over 45 seconds
    useEffect(() => {
        if (processing && progress['_backup'] === 'processing') {
            const startTime = Date.now();
            const duration = 45000; // 45 seconds
            const targetProgress = 25;

            const animateProgress = () => {
                const elapsed = Date.now() - startTime;
                const progress = Math.min((elapsed / duration) * targetProgress, targetProgress);
                setBackupProgress(progress);

                if (progress < targetProgress && processing) {
                    requestAnimationFrame(animateProgress);
                }
            };

            requestAnimationFrame(animateProgress);
        } else if (progress['_backup'] === 'completed' || progress['_backup'] === 'error') {
            // Reset or set to 25% when backup completes
            setBackupProgress(25);
        }
    }, [processing, progress]);

    const loadMetadata = async () => {
        setLoadingMetadata(true);
        try {
            // Only load attributes and categories for retailers
            if (isRetailer) {                
                const attributesResult = await utilGetAttributes(null, isRetailer);
                if (!attributesResult.errorMessage && Array.isArray(attributesResult)) {
                    setAvailableAttributes(attributesResult);
                }

                const categoriesResult = await getAllCategories(null, isRetailer);
                if (Array.isArray(categoriesResult)) {
                    setAvailableCategories(categoriesResult);
                }
            }

            // Load tags (available for both retailers and suppliers)
            const tagsResult = await getAllTags(currentCatalog === "partnercatalog", isRetailer);
            if (Array.isArray(tagsResult)) {
                setAvailableTags(tagsResult);
            }
        } catch (error) {
            console.error("Error loading metadata:", error);
        }
        setLoadingMetadata(false);
    };

    const renderValueInput = (attribute, operator, value, onChange) => {
        if (operator === "clear" || operator === "set_yes" || operator === "set_no") {
            return <Typography>No value required</Typography>;
        }

        switch (attribute.Type) {
            case "list":
                if (operator === "replace") {
                    return (
                        <EnhancedSelect
                            placeholder="Select value"
                            value={value ? { label: value, value: value } : null}
                            options={attribute.Options?.map(option => ({
                                label: option,
                                value: option
                            })) || []}
                            onChange={(selectedOption) => onChange(selectedOption?.value || "")}
                        />
                    );
                } else if (operator === "add") {
                    return (
                        <EnhancedAutocomplete
                            value={value || []}
                            options={attribute.Options || []}
                            onChange={(e, newValue) => onChange(newValue)}
                            multiple
                        />
                    );
                }
                break;
            case "text":
                return (
                    <EnhancedTextField
                        placeholder="Enter value"
                        value={value || ""}
                        onChange={(e) => onChange(e.target.value)}
                    />
                );
            case "number":
                return (
                    <EnhancedTextField
                        placeholder="Enter number"
                        type="number"
                        value={value || ""}
                        onChange={(e) => onChange(e.target.value)}
                    />
                );
            case "boolean":
            case "yesno":
                if (operator === "replace") {
                    return (
                        <EnhancedSelect
                            placeholder="Select value"
                            value={value !== undefined ? { 
                                label: value === true || value === "true" || value === "yes" ? "Yes" : "No", 
                                value: value 
                            } : null}
                            options={[
                                { label: "Yes", value: true },
                                { label: "No", value: false }
                            ]}
                            onChange={(selectedOption) => onChange(selectedOption?.value)}
                        />
                    );
                }
                return <Typography>Value will be set automatically</Typography>;
            case "tags":
                if (operator === "replace" || operator === "add" || operator === "remove") {
                    return (
                        <EnhancedAutocomplete
                            value={value || []}
                            options={availableTags}
                            onChange={(e, newValue) => onChange(newValue)}
                            multiple
                            freeSolo
                            autoSelect
                        />
                    );
                }
                break;
            case "category":
                if (operator === "replace") {
                    return (
                        <EnhancedSelect
                            value={value ? { 
                                label: availableCategories.find(cat => cat.Id === value)?.Path || value,
                                value: value 
                            } : null}
                            options={availableCategories.map(category => ({
                                label: category.Path || category.Name,
                                value: category.Id
                            }))}
                            onChange={(selectedOption) => onChange(selectedOption?.value || "")}
                        />
                    );
                }
                break;
            case "video":
                return (
                    <EnhancedTextField
                        placeholder="Enter YouTube or Vimeo URL"
                        value={value || ""}
                        onChange={(e) => onChange(e.target.value)}
                        helperText="e.g., https://www.youtube.com/watch?v=... or https://vimeo.com/..."
                    />
                );
            case "document":
                return (
                    <EnhancedTextField
                        placeholder="Enter document URL"
                        value={value || ""}
                        onChange={(e) => onChange(e.target.value)}
                        helperText="PDF, DOC, XLSX, etc. or Google Drive/Dropbox/OneDrive link"
                    />
                );
            default:
                return (
                    <EnhancedTextField
                        placeholder="Enter value"
                        value={value || ""}
                        onChange={(e) => onChange(e.target.value)}
                    />
                );
        }
    };

    const handleClose = () => {
        setOpen(false);
        setActiveStep(0);
        setSelectedAttributes([]);
        setAttributeOperations({});
        setProcessing(false);
        setProgress({});
        setCompleted(false);
        setProductPreviews([]);
        setLoadingPreviews(false);
        setAttributeSearchTerm("");
        setBackupProgress(0);
    };

    const loadProductPreviews = async () => {
        setLoadingPreviews(true);
        const previews = [];
        
        for (const product of selectedProducts.slice(0, 10)) { // Limit to 10 products for performance
            try {
                const partnerId = isRetailer ? product.supplierId : product.merchantId;
                const isPartner = currentCatalog === "partnercatalog";
                
                const fullProductData = await getProduct(product.sku, partnerId, isPartner, isRetailer);
                
                if (!fullProductData.errorMessage) {
                    const attributeChanges = {};
                    
                    selectedAttributes.forEach(attribute => {
                        const operation = attributeOperations[attribute.Name];
                        if (operation && operation.operator) {
                            const currentValue = fullProductData.Attributes?.[attribute.Name];
                            const newValue = applyOperation(
                                currentValue, 
                                operation.operator, 
                                operation.value, 
                                attribute.Type
                            );
                            
                            // Special handling for category preview
                            if (attribute.Name === 'category' && attribute.Type === 'category') {
                                if (operation.operator === 'clear') {
                                    attributeChanges.category = {
                                        oldValue: fullProductData.Attributes?.category,
                                        newValue: null,
                                        type: 'text'
                                    };
                                    attributeChanges.category_id = {
                                        oldValue: fullProductData.Attributes?.category_id,
                                        newValue: null,
                                        type: 'text'
                                    };
                                } else if (operation.operator === 'replace' && operation.value) {
                                    const selectedCategory = availableCategories.find(cat => cat.Id === operation.value);
                                    if (selectedCategory) {
                                        attributeChanges.category = {
                                            oldValue: fullProductData.Attributes?.category,
                                            newValue: selectedCategory.Name,
                                            type: 'text'
                                        };
                                        attributeChanges.category_id = {
                                            oldValue: fullProductData.Attributes?.category_id,
                                            newValue: selectedCategory.Id,
                                            type: 'text'
                                        };
                                    }
                                }
                            } else {
                                attributeChanges[attribute.Name] = {
                                    oldValue: currentValue,
                                    newValue: newValue,
                                    type: attribute.Type
                                };
                            }
                        }
                    });
                    
                    previews.push({
                        sku: product.sku,
                        changes: attributeChanges
                    });
                }
            } catch (error) {
                console.error(`Error loading preview for ${product.sku}:`, error);
            }
        }
        
        setProductPreviews(previews);
        setLoadingPreviews(false);
    };

    const handleNext = async () => {
        if (activeStep === 1 && canProceed()) {
            setTransitioning(true);
            await loadProductPreviews();
            setTransitioning(false);
        }
        setActiveStep(activeStep + 1);
    };

    const handleComplete = () => {
        handleClose();
        if (onComplete) onComplete();
    };

    const handleViewBackups = () => {
        const baseDate = backupTimestamp || new Date();
        const thirtyDaysAgo = new Date(baseDate);
        thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
        
        const formattedDate = encodeURIComponent(thirtyDaysAgo.toString());
        const currentCompanyName = Auth.CompanyName;
        let queryParams = [`q.0=CreateDate%20gt%20${formattedDate}`, `q.1=Type%20eq%20poc_product_backup.csv`];
        
        if (currentCompanyName) {
            queryParams.push(`q.2=CompanyName%20eq%20${encodeURIComponent(currentCompanyName)}`);
            queryParams.push(`q.3=PartnerCompanyName%20eq%20${encodeURIComponent(currentCompanyName)}`);
        }
        
        const url = `/attachment-list?${queryParams.join('&')}`;
        window.open(url, '_blank');
    };

    const handleBack = () => {
        setActiveStep(activeStep - 1);
    };

    const handleAttributeToggle = (attribute) => {
        setSelectedAttributes(prev => {
            const isSelected = prev.some(attr => attr.Name === attribute.Name);
            if (isSelected) {
                // Remove attribute and its operations
                const newOperations = { ...attributeOperations };
                delete newOperations[attribute.Name];
                setAttributeOperations(newOperations);
                return prev.filter(attr => attr.Name !== attribute.Name);
            } else {
                return [...prev, attribute];
            }
        });
    };

    const handleOperationChange = (attributeName, field, value) => {
        setAttributeOperations(prev => ({
            ...prev,
            [attributeName]: {
                ...prev[attributeName],
                [field]: value
            }
        }));
    };

    const executeOperations = async () => {
        setProcessing(true);
        setActiveStep(3);
        setBackupProgress(0);
        
        const errors = [];

        // Create backup before applying changes
        let backupSuccessful = false;
        try {
            setProgress(prev => ({
                ...prev,
                '_backup': "processing"
            }));
            
            const backupResult = await createAndUploadProductBackup(
                selectedProducts, 
                selectedAttributes, 
                isRetailer, 
                currentCatalog
            );
            
            if (backupResult.success) {
                setProgress(prev => ({
                    ...prev,
                    '_backup': "completed"
                }));
                backupSuccessful = true;
                setBackupTimestamp(new Date());
            } else {
                setProgress(prev => ({
                    ...prev,
                    '_backup': "error"
                }));
                errors.push({ sku: 'BACKUP', error: `Failed to create backup: ${backupResult.error}` });
            }
        } catch (error) {
            setProgress(prev => ({
                ...prev,
                '_backup': "error"
            }));
            errors.push({ sku: 'BACKUP', error: `Failed to create backup: ${error.message}` });
        }

        // Only proceed with updates if backup was successful
        if (!backupSuccessful) {
            // Mark all products as canceled since backup failed
            selectedProducts.forEach(product => {
                setProgress(prev => ({
                    ...prev,
                    [product.sku]: "canceled"
                }));
            });
            
            setError({
                open: true,
                errorHeader: "Backup failed",
                errorMessage: "Product updates were not performed because the backup could not be created. Please try again."
            });
            setCompleted(true);
            setProcessing(false);
            return;
        }

        // Process products in batches of 10
        const batchSize = 10;
        const batches = [];
        
        for (let i = 0; i < selectedProducts.length; i += batchSize) {
            batches.push(selectedProducts.slice(i, i + batchSize));
        }

        for (const batch of batches) {
            const batchPromises = batch.map(async (product) => {
                try {
                    setProgress(prev => ({
                        ...prev,
                        [product.sku]: "processing"
                    }));

                    const partnerId = isRetailer ? product.supplierId : product.merchantId;
                    const isPartner = currentCatalog === "partnercatalog";
                    
                    const fullProductData = await getProduct(product.sku, partnerId, isPartner, isRetailer);
                    
                    if (fullProductData.errorMessage) {
                        errors.push({ sku: product.sku, error: fullProductData.errorMessage });
                        setProgress(prev => ({
                            ...prev,
                            [product.sku]: "error"
                        }));
                        return;
                    }
                    
                    if (!fullProductData.Attributes) {
                        fullProductData.Attributes = {};
                    }
                    
                    selectedAttributes.forEach(attribute => {
                        const operation = attributeOperations[attribute.Name];
                        if (operation && operation.operator) {
                            const currentValue = fullProductData.Attributes[attribute.Name];
                            const newValue = applyOperation(
                                currentValue, 
                                operation.operator, 
                                operation.value, 
                                attribute.Type
                            );
                            
                            // Special handling for category
                            if (attribute.Name === 'category' && attribute.Type === 'category') {
                                if (operation.operator === 'clear') {
                                    fullProductData.Attributes.category = null;
                                    fullProductData.Attributes.category_id = null;
                                } else if (operation.operator === 'replace' && operation.value) {
                                    const selectedCategory = availableCategories.find(cat => cat.Id === operation.value);
                                    if (selectedCategory) {
                                        fullProductData.Attributes.category = selectedCategory.Name;
                                        fullProductData.Attributes.category_id = selectedCategory.Id;
                                    }
                                }
                            } else {
                                fullProductData.Attributes[attribute.Name] = newValue;
                            }
                        }
                    });

                    const result = await updateProduct(fullProductData, product.sku, partnerId, isPartner, isRetailer);
                    
                    if (result.errorMessage) {
                        errors.push({ sku: product.sku, error: result.errorMessage });
                        setProgress(prev => ({
                            ...prev,
                            [product.sku]: "error"
                        }));
                    } else {
                        setProgress(prev => ({
                            ...prev,
                            [product.sku]: "completed"
                        }));
                    }
                } catch (error) {
                    errors.push({ sku: product.sku, error: error.message });
                    setProgress(prev => ({
                        ...prev,
                        [product.sku]: "error"
                    }));
                }
            });

            // Wait for all products in the current batch to complete
            await Promise.all(batchPromises);
        }

        if (errors.length > 0) {
            setError({
                open: true,
                errorHeader: "Some products failed to update",
                errorMessage: errors.map(e => `${e.sku}: ${e.error}`).join("\n")
            });
        }

        setCompleted(true);
        setProcessing(false);
        
        // Note: onComplete will be called when user clicks "Close" to refresh the table
    };

    const renderStepContent = () => {
        switch (activeStep) {
            case 0: // Select Attributes
                const filterAttributes = (attr) => {
                    if (BULK_UPDATE_EXCLUDED_ATTRIBUTES.includes(attr.Name)) {
                        return false;
                    }
                    
                    if (!attributeSearchTerm) return true;
                    const searchLower = attributeSearchTerm.toLowerCase();
                    const friendlyName = (attr.FriendlyName || attr.Name || "").toLowerCase();
                    const name = (attr.Name || "").toLowerCase();
                    const type = (attr.Type || "").toLowerCase();
                    return friendlyName.includes(searchLower) || name.includes(searchLower) || type.includes(searchLower);
                };

                const specialAttributes = [
                    { Name: "tags", Type: "tags", FriendlyName: "Tags" },
                    { Name: "category", Type: "category", FriendlyName: "Category" }
                ];

                const filteredSpecialAttributes = specialAttributes.filter(attr => {
                    if (!attributeSearchTerm) return true;
                    const searchLower = attributeSearchTerm.toLowerCase();
                    const friendlyName = (attr.FriendlyName || attr.Name || "").toLowerCase();
                    const name = (attr.Name || "").toLowerCase();
                    const type = (attr.Type || "").toLowerCase();
                    return friendlyName.includes(searchLower) || name.includes(searchLower) || type.includes(searchLower);
                });

                const filteredAttributes = availableAttributes.filter(filterAttributes);

                return (
                    <div className={classes.stepContent}>
                        <Typography variant="h6" gutterBottom>
                            Select Attributes to Edit
                        </Typography>
                        <Typography variant="body2" color="textSecondary" gutterBottom>
                            Choose which attributes you want to modify for the selected products.
                        </Typography>
                        
                        {/* Search Bar */}
                        <OutlinedInput 
                            placeholder="Search attributes..."
                            value={attributeSearchTerm}
                            onChange={(e) => setAttributeSearchTerm(e.target.value)}
                            className={clsx(globalClasses.search, classes.searchBar)}
                            startAdornment={
                                <InputAdornment position="start">
                                    <SearchIcon />
                                </InputAdornment>
                            }
                            endAdornment={
                                attributeSearchTerm && (
                                    <InputAdornment 
                                        style={{ cursor: "pointer" }} 
                                        position="end"
                                        onClick={() => setAttributeSearchTerm("")}
                                    >
                                        <Clear style={{ width: "20px", height: "20px", opacity: "0.8" }} />
                                    </InputAdornment>
                                )
                            }
                        />
                        
                        {loadingMetadata && (
                            <Box display="flex" justifyContent="center" padding={2}>
                                <CircularProgress size={24} />
                                <Typography variant="body2" style={{ marginLeft: "8px" }}>
                                    Loading available attributes...
                                </Typography>
                            </Box>
                        )}
                        
                        <Grid container style={{ marginTop: "8px", overflowY: "auto", gap: "8px" }}>
                            {/* Special attributes - Tags and Categories */}
                            {filteredSpecialAttributes.map((attribute) => (
                                <Grid item style={{ flex: "1 1 25%", maxWidth: "calc(33% - 16px)" }} key={attribute.Name}>
                                    <Paper className={classes.attributeCard}>
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    checked={selectedAttributes.some(attr => attr.Name === attribute.Name)}
                                                    onChange={() => handleAttributeToggle(attribute)}
                                                />
                                            }
                                            label={
                                                <div>
                                                    <Typography variant="subtitle2">
                                                        {attribute.FriendlyName}
                                                    </Typography>
                                                    <Typography variant="caption" color="textSecondary">
                                                        Type: {attribute.Type}
                                                    </Typography>
                                                </div>
                                            }
                                        />
                                    </Paper>
                                </Grid>
                            ))}
                            
                            {/* Regular attributes */}
                            {filteredAttributes.map((attribute) => (
                                <Grid item style={{ flex: "1 1 25%", maxWidth: "calc(33% - 16px)" }} key={attribute.Name}>
                                    <Paper className={classes.attributeCard}>
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    checked={selectedAttributes.some(attr => attr.Name === attribute.Name)}
                                                    onChange={() => handleAttributeToggle(attribute)}
                                                />
                                            }
                                            label={
                                                <div>
                                                    <Typography variant="subtitle2">
                                                        {attribute.FriendlyName || attribute.Name}
                                                    </Typography>
                                                    <Typography variant="caption" color="textSecondary">
                                                        Type: {attribute.Type}
                                                    </Typography>
                                                </div>
                                            }
                                        />
                                    </Paper>
                                </Grid>
                            ))}
                        </Grid>

                        {/* Show message if no attributes match the search */}
                        {attributeSearchTerm && filteredSpecialAttributes.length === 0 && filteredAttributes.length === 0 && (
                            <Box display="flex" justifyContent="center" padding={4}>
                                <Typography variant="body2" color="textSecondary">
                                    No attributes found matching "{attributeSearchTerm}"
                                </Typography>
                            </Box>
                        )}
                    </div>
                );

            case 1: // Configure Operations
                return (
                    <div className={classes.stepContent}>
                        <Typography variant="h6" gutterBottom>
                            Configure Operations
                        </Typography>
                        <Typography variant="body2" color="textSecondary" gutterBottom>
                            Define how each attribute should be modified.
                        </Typography>

                        {selectedAttributes.map((attribute) => (
                            <Paper key={attribute.Name} className={classes.attributeCard}>
                                <Grid container spacing={2} alignItems="center">
                                    <Grid item xs={4}>
                                        <Typography variant="subtitle1">
                                            {attribute.FriendlyName || attribute.Name}
                                        </Typography>
                                        <Typography variant="caption" color="textSecondary">
                                            {attribute.Type}
                                        </Typography>
                                    </Grid>
                                    
                                    <Grid item xs={3} className={classes.operatorColumn}>
                                        <EnhancedSelect
                                            placeholder="Select operation"
                                            value={attributeOperations[attribute.Name]?.operator ? {
                                                label: getOperatorsByType(attribute.Type).find(op => op.value === attributeOperations[attribute.Name]?.operator)?.label,
                                                value: attributeOperations[attribute.Name]?.operator
                                            } : null}
                                            options={getOperatorsByType(attribute.Type)}
                                            onChange={(option) => handleOperationChange(attribute.Name, 'operator', option?.value)}
                                        />
                                    </Grid>
                                    
                                    <Grid item xs={5} className={classes.valueColumn}>
                                        {attributeOperations[attribute.Name]?.operator && 
                                            renderValueInput(
                                                attribute,
                                                attributeOperations[attribute.Name]?.operator,
                                                attributeOperations[attribute.Name]?.value,
                                                (value) => handleOperationChange(attribute.Name, 'value', value)
                                            )
                                        }
                                    </Grid>
                                </Grid>
                            </Paper>
                        ))}
                    </div>
                );

            case 2: // Confirmation
                return (
                    <div className={classes.stepContent}>
                        <Typography variant="h6" gutterBottom>
                            Confirmation
                        </Typography>
                        <Typography variant="body2" color="textSecondary" gutterBottom>
                            Review the changes that will be applied to {selectedProducts.length} products.
                        </Typography>
                        <Alert severity="info" style={{ marginTop: "8px" }}>
                            A backup CSV file will be automatically created before applying changes.
                        </Alert>

                        {loadingPreviews ? (
                            <Box display="flex" justifyContent="center" padding={4}>
                                <CircularProgress />
                            </Box>
                        ) : (
                            <>
                                {/* Summary of operations */}
                                <Paper style={{ padding: "16px", marginTop: "16px" }}>
                                    <Typography variant="subtitle2" gutterBottom>
                                        Operations to Apply:
                                    </Typography>
                                    {selectedAttributes.map((attribute) => {
                                        const operation = attributeOperations[attribute.Name];
                                        const operatorLabel = getOperatorsByType(attribute.Type).find(op => op.value === operation?.operator)?.label;
                                        
                                        let displayValue = operation?.value;
                                        // For category, show the category name instead of ID
                                        if (attribute.Name === 'category' && attribute.Type === 'category' && operation?.value) {
                                            const selectedCategory = availableCategories.find(cat => cat.Id === operation.value);
                                            displayValue = selectedCategory ? selectedCategory.Name : operation.value;
                                        }
                                        
                                        return (
                                            <Typography key={attribute.Name} variant="body2" style={{ marginTop: "8px" }}>
                                                <strong>{attribute.FriendlyName || attribute.Name}:</strong> {operatorLabel}
                                                {operation?.operator !== "clear" && operation?.operator !== "set_yes" && operation?.operator !== "set_no" && (
                                                    <span> ({Array.isArray(displayValue) ? displayValue.join(", ") : displayValue})</span>
                                                )}
                                            </Typography>
                                        );
                                    })}
                                </Paper>

                                {/* Diff table showing preview */}
                                <TableContainer component={Paper} className={classes.diffTable}>
                                    <Table>
                                        <TableHead>
                                            <TableRow>
                                                <TableCell className={classes.skuCell}>SKU</TableCell>
                                                {selectedAttributes.map((attribute) => (
                                                    <TableCell key={attribute.Name} align="center">
                                                        {attribute.FriendlyName || attribute.Name}
                                                    </TableCell>
                                                ))}
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {productPreviews.map((preview, index) => (
                                                <TableRow key={`${preview.sku}-preview-${index}`}>
                                                    <TableCell className={classes.skuCell}>
                                                        {preview.sku}
                                                    </TableCell>
                                                    {selectedAttributes.map((attribute) => {
                                                        const change = preview.changes[attribute.Name];
                                                        
                                                        if (!change) {
                                                            return <TableCell key={attribute.Name} align="center">-</TableCell>;
                                                        }
                                                        
                                                        const formatValue = (value, type) => {
                                                            if (value === null || value === undefined) return "—";
                                                            if (type === "boolean" || type === "yesno") return value ? "Yes" : "No";
                                                            if (type === "number" && !isNaN(value)) {
                                                                return parseFloat(value).toLocaleString(undefined, { maximumFractionDigits: 2 });
                                                            }
                                                            if (Array.isArray(value)) return value.join(", ");
                                                            return String(value);
                                                        };
                                                        
                                                        return (
                                                            <TableCell key={attribute.Name} align="center">
                                                                {change.oldValue !== change.newValue ? (
                                                                    <>
                                                                        <span className={classes.diffOldValue}>
                                                                            {formatValue(change.oldValue, change.type)}
                                                                        </span>
                                                                        <span className={classes.diffNewValue}>
                                                                            {formatValue(change.newValue, change.type)}
                                                                        </span>
                                                                    </>
                                                                ) : (
                                                                    <span>{formatValue(change.newValue, change.type)}</span>
                                                                )}
                                                            </TableCell>
                                                        );
                                                    })}
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>

                                {productPreviews.length > 0 && productPreviews.length < selectedProducts.length && (
                                    <Typography variant="caption" className={classes.previewNote}>
                                        * Showing preview for {productPreviews.length} of {selectedProducts.length} products
                                    </Typography>
                                )}

                                {selectedProducts.length > 10 && (
                                    <Box style={{ marginTop: "16px" }}>
                                        <Typography variant="subtitle2">
                                            All products to be updated ({selectedProducts.length}):
                                        </Typography>
                                        <Box style={{ maxHeight: "150px", overflow: "auto", marginTop: "8px" }}>
                                            {selectedProducts.map((product, index) => (
                                                <Chip
                                                    key={`${product.sku}-chip-${index}`}
                                                    label={product.sku}
                                                    size="small"
                                                    style={{ margin: "2px" }}
                                                />
                                            ))}
                                        </Box>
                                    </Box>
                                )}
                            </>
                        )}
                    </div>
                );

            case 3: // Processing
                return (
                    <div className={classes.progressContainer} style={{ minHeight: 0 }}>
                        <Typography variant="h6" gutterBottom>
                            {completed ? "Update Complete" : "Updating Products..."}
                        </Typography>
                        
                        {!completed && (
                            <LinearProgress 
                                variant="determinate" 
                                value={(() => {
                                    // Calculate overall progress
                                    // Backup takes up to 25% of the progress
                                    // Products take the remaining 75%
                                    const backupProgressValue = progress['_backup'] === 'processing' ? backupProgress : 
                                                              progress['_backup'] === 'completed' ? 25 : 
                                                              progress['_backup'] === 'error' ? 25 : 0;
                                    
                                    const completedProducts = Object.keys(progress).filter(key => 
                                        key !== '_backup' && (progress[key] === 'completed' || progress[key] === 'error' || progress[key] === 'canceled')
                                    ).length;
                                    
                                    const productProgress = (completedProducts / selectedProducts.length) * 75;
                                    
                                    return backupProgressValue + productProgress;
                                })()}
                                style={{ 
                                    marginBottom: "20px",
                                    height: "14px",
                                    borderRadius: "4px"
                                }}
                            />
                        )}

                        {completed && (
                            <Box style={{ marginBottom: "16px" }}>
                                {(() => {
                                    const completedCount = selectedProducts.filter(p => progress[p.sku] === "completed").length;
                                    const errorCount = selectedProducts.filter(p => progress[p.sku] === "error").length;
                                    const processingCount = selectedProducts.filter(p => progress[p.sku] === "processing").length;
                                    const canceledCount = selectedProducts.filter(p => progress[p.sku] === "canceled").length;
                                    const pendingCount = selectedProducts.filter(p => !progress[p.sku] || progress[p.sku] === "pending").length;
                                    const backupStatus = progress['_backup'];

                                    return (
                                        <div>
                                            <Typography variant="body1" style={{ marginBottom: "8px" }}>
                                                <strong>Summary:</strong> {completedCount} successful, {errorCount} failed
                                                {canceledCount > 0 && `, ${canceledCount} canceled`}
                                                {processingCount > 0 && `, ${processingCount} still processing`}
                                                {pendingCount > 0 && `, ${pendingCount} pending`}
                                                {backupStatus === "completed" && " • Backup created successfully"}
                                                {backupStatus === "error" && " • Backup failed"}
                                            </Typography>
                                            {errorCount > 0 && (
                                                <Alert severity="warning">
                                                    {errorCount} product(s) failed to update. Check the details below.
                                                </Alert>
                                            )}
                                            {completedCount === selectedProducts.length && (
                                                <Alert severity="success">
                                                    All products updated successfully!
                                                </Alert>
                                            )}
                                            {canceledCount > 0 && backupStatus === "error" && (
                                                <Alert severity="error">
                                                    All product updates were canceled because the backup could not be created.
                                                </Alert>
                                            )}
                                            {(processingCount > 0 || pendingCount > 0) && (
                                                <Alert severity="info">
                                                    Some products are still being processed...
                                                </Alert>
                                            )}
                                        </div>
                                    );
                                })()}
                            </Box>
                        )}

                        <Box style={{ flexGrow: 1, overflow: "auto", marginTop: "16px" }}>
                            {/* Show backup status first */}
                            <div className={classes.progressItem}>
                                <Typography variant="body2" style={{ fontWeight: 'bold' }}>Creating Backup</Typography>
                                <div>
                                    {progress['_backup'] === "completed" && <Typography color="primary">✓ Backup Created</Typography>}
                                    {progress['_backup'] === "error" && <Typography color="error">✗ Backup Failed</Typography>}
                                    {progress['_backup'] === "processing" && <Typography>Creating backup...</Typography>}
                                    {!progress['_backup'] && <Typography color="textSecondary">Pending</Typography>}
                                </div>
                            </div>
                            
                            {/* Show product progress - Always show all products */}
                            {selectedProducts.map((product, index) => {
                                const status = progress[product.sku];
                                return (
                                    <div key={`${product.sku}-${index}`} className={classes.progressItem}>
                                        <Typography variant="body2">{product.sku}</Typography>
                                        <div>
                                            {status === "completed" && <Typography color="primary">✓ Completed</Typography>}
                                            {status === "error" && <Typography color="error">✗ Error</Typography>}
                                            {status === "processing" && <Typography>Processing...</Typography>}
                                            {status === "canceled" && <Typography color="textSecondary">Canceled</Typography>}
                                            {!status && <Typography color="textSecondary">Pending</Typography>}
                                        </div>
                                    </div>
                                );
                            })}
                        </Box>
                    </div>
                );

            default:
                return null;
        }
    };

    const canProceed = () => {
        switch (activeStep) {
            case 0:
                return selectedAttributes.length > 0;
            case 1:
                return selectedAttributes.every(attr => 
                    attributeOperations[attr.Name]?.operator &&
                    (attributeOperations[attr.Name]?.operator === "clear" || 
                     attributeOperations[attr.Name]?.operator === "set_yes" || 
                     attributeOperations[attr.Name]?.operator === "set_no" ||
                     (attributeOperations[attr.Name]?.value !== undefined && 
                      (attr.Type !== "tags" || attributeOperations[attr.Name]?.value?.length > 0)))
                );
            case 2:
                return true;
            default:
                return false;
        }
    };

    return (
        <Dialog open={open} onClose={handleClose} className={classes.dialog} maxWidth={false}>
            <DialogTitle>
                Bulk Edit Products ({selectedProducts.length} selected)
            </DialogTitle>
            
            <DialogContent dividers>
                <Stepper activeStep={activeStep} alternativeLabel>
                    {steps.map((label) => (
                        <Step key={label}>
                            <StepLabel>{label}</StepLabel>
                        </Step>
                    ))}
                </Stepper>
                
                {renderStepContent()}
            </DialogContent>
            
            <DialogActions>
                {activeStep < 3 && (
                    <Button onClick={handleClose} disabled={transitioning}>
                        Cancel
                    </Button>
                )}
                
                {activeStep > 0 && activeStep < 3 && (
                    <Button onClick={handleBack} disabled={transitioning}>
                        Back
                    </Button>
                )}
                
                {activeStep < 2 && (
                    <Button
                        onClick={handleNext}
                        disabled={!canProceed() || transitioning}
                        variant="contained"
                        color="primary"
                    >
                        {transitioning ? <CircularProgress size={24} color="inherit" /> : "Next"}
                    </Button>
                )}
                
                {activeStep === 2 && (
                    <Button
                        onClick={executeOperations}
                        disabled={!canProceed()}
                        variant="contained"
                        color="primary"
                    >
                        Execute Operations
                    </Button>
                )}
                
                {completed && (
                    <>
                        {progress['_backup'] === "completed" && (
                            <Button
                                onClick={handleViewBackups}
                                variant="outlined"
                                color="primary"
                                style={{ marginRight: '8px' }}
                            >
                                View Backups
                            </Button>
                        )}
                        <Button
                            onClick={handleComplete}
                            variant="contained"
                            color="primary"
                        >
                            Close
                        </Button>
                    </>
                )}
            </DialogActions>
        </Dialog>
    );
}

export default BulkEditActions; 